// src/components/quiz/ModuleQuizComponent.tsx
// SIMPLIFIED VERSION - Single progress bar, cleaner UI
// FIXED: Type 13 (True/False Selection) validation and state handling

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  Suspense,
  useRef,
} from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  NavigateNext,
  NavigateBefore,
  CheckCircle,
  ExitToApp,
  Warning,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  submitQuizAnswers,
  QuizSubmissionData,
  QuizResult,
} from "../../services/moduleQuizService";
import { v4 as uuidv4 } from "uuid";
import QuestionRenderer from "./QuestionRenderer";
import {
  quizStorageService,
  QuizState,
  SaveResult,
} from "../../services/quiz/quizStorageService";
import { AnswerStateManager } from "../../services/quiz/answerStateManager";

interface StorageNotification {
  open: boolean;
  message: string;
  severity: "success" | "warning" | "error" | "info";
  autoHideDuration?: number;
}

interface ModuleQuizComponentProps {
  questions: any[];
  moduleId: string;
  moduleName: string;
  week: number;
  initialState?: QuizState | null;
  onComplete: (results: any) => void;
  onExit: () => void;
}

const ModuleQuizComponent: React.FC<ModuleQuizComponentProps> = ({
  questions,
  moduleId,
  moduleName,
  week,
  initialState,
  onComplete,
  onExit,
}) => {
  const theme = useTheme();

  // Initialize AnswerStateManager
  const answerManagerRef = useRef<AnswerStateManager>(
    new AnswerStateManager(initialState?.userAnswers)
  );

  // Refs to track state
  const startTimeRef = useRef(initialState?.startTime ?? Date.now());
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Generate deterministic quiz ID for state persistence
  const quizId = `${moduleName}_week${week}`.replace(/[^a-zA-Z0-9_]/g, "_");

  // Initialize answers properly based on question type
  const initializeAnswers = (questions: any[]) => {
    const initialAnswers: Record<string, any> = {};
    questions.forEach((question) => {
      switch (question.type) {
        case "4":
          initialAnswers[question.id] = [];
          break;
        case "6":
          initialAnswers[question.id] = {};
          break;
        case "12":
          initialAnswers[question.id] = "VIEWED";
          break;
        case "8":
        case "3":
          initialAnswers[question.id] = [];
          break;
        // FIX: Type 13 uses object format { "statement-0": true, "statement-1": false, ... }
        case "13":
          initialAnswers[question.id] = {};
          break;
        case "9":
          initialAnswers[question.id] = {};
          break;
        default:
          initialAnswers[question.id] = undefined;
      }
    });
    return initialAnswers;
  };

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    initialState?.currentQuestionIndex ?? 0
  );
  const [userAnswers, setUserAnswers] = useState(() => {
    if (initialState) {
      const restored: Record<string, any> = {};
      initialState.userAnswers.forEach((value, key) => {
        restored[key] = value;
      });
      return restored;
    }
    return initializeAnswers(questions);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);

  // Storage-related states
  const [storageWarning, setStorageWarning] = useState(false);
  const [storageNotification, setStorageNotification] =
    useState<StorageNotification>({
      open: false,
      message: "",
      severity: "info",
    });
  const [lastSaveStatus, setLastSaveStatus] = useState<
    "saving" | "saved" | "error" | null
  >(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // Computed values
  const validQuestions = useMemo(
    () => questions.filter((q) => q && q.id),
    [questions]
  );
  const currentQuestion = validQuestions[currentQuestionIndex] || null;
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex >= validQuestions.length - 1;

  // Progress statistics
  const progressStats = useMemo(() => {
    const completed = validQuestions.filter((q) => {
      const answer = userAnswers[q.id];

      if (q.type === "12") return true;

      // FIX: Type 13 - check if all statements are answered
      if (q.type === "13") {
        if (typeof answer !== "object" || Array.isArray(answer)) return false;
        const totalStatements = q.statements?.length || 0;
        if (totalStatements === 0) return true;
        const answeredCount = Object.values(answer).filter(
          (v) => v === true || v === false
        ).length;
        return answeredCount === totalStatements;
      }

      if (Array.isArray(answer)) {
        return answer.length > 0;
      }

      return (
        answer !== undefined &&
        answer !== null &&
        answer !== "" &&
        answer !== "SKIPPED" &&
        !(typeof answer === "object" && Object.keys(answer).length === 0)
      );
    }).length;

    const skipped = validQuestions.filter((q) => {
      const answer = userAnswers[q.id];
      return answer === "SKIPPED";
    }).length;

    const required = validQuestions.filter((q) => q.required !== false).length;

    const requiredCompleted = validQuestions
      .filter((q) => q.required !== false)
      .filter((q) => {
        const answer = userAnswers[q.id];

        if (q.type === "12") return true;

        // FIX: Type 13 - check if all statements are answered
        if (q.type === "13") {
          if (typeof answer !== "object" || Array.isArray(answer)) return false;
          const totalStatements = q.statements?.length || 0;
          if (totalStatements === 0) return true;
          const answeredCount = Object.values(answer).filter(
            (v) => v === true || v === false
          ).length;
          return answeredCount === totalStatements;
        }

        if (Array.isArray(answer)) {
          return answer.length > 0;
        }

        return (
          answer !== undefined &&
          answer !== null &&
          answer !== "" &&
          answer !== "SKIPPED" &&
          !(typeof answer === "object" && Object.keys(answer).length === 0)
        );
      }).length;

    return {
      completed,
      skipped,
      required,
      requiredCompleted,
      total: validQuestions.length,
    };
  }, [userAnswers, validQuestions]);

  // Single completion percentage
  const completionPercentage = useMemo(() => {
    if (validQuestions.length === 0) return 0;
    return (progressStats.completed / validQuestions.length) * 100;
  }, [progressStats.completed, validQuestions.length]);

  // Check if all required questions are done
  const allRequiredDone =
    progressStats.requiredCompleted === progressStats.required;
  const missingRequired =
    progressStats.required - progressStats.requiredCompleted;

  // Question validation
  const isCurrentQuestionValid = useMemo(() => {
    if (!currentQuestion) return false;

    const userAnswer = userAnswers[currentQuestion.id];
    const questionType = currentQuestion.type;
    const isRequired = currentQuestion.required !== false;

    // Type 12 (Instructional) - always valid
    if (questionType === "12") return true;

    // Skipped optional questions are valid
    if (userAnswer === "SKIPPED" && !isRequired) return true;

    // Optional questions with no answer - allow navigation
    if (
      !isRequired &&
      (userAnswer === undefined || userAnswer === null || userAnswer === "")
    ) {
      return true;
    }

    // Required questions must have an answer
    if (
      isRequired &&
      (userAnswer === undefined || userAnswer === null || userAnswer === "")
    ) {
      return false;
    }

    // ============================================================
    // TYPE-SPECIFIC VALIDATION
    // ============================================================
    switch (questionType) {
      // Type 4: Sentence Building - ALL words must be placed
      case "4": {
        if (!Array.isArray(userAnswer)) return false;
        const totalWords = currentQuestion.wordOptions?.length || 0;
        return userAnswer.length === totalWords;
      }

      // Type 6: Reference Reading - ALL sub-questions must be answered
      case "6": {
        if (typeof userAnswer !== "object" || Array.isArray(userAnswer))
          return false;
        const totalQuestions = currentQuestion.questions?.length || 0;
        if (totalQuestions === 0) return true;
        const answeredCount = Object.values(userAnswer).filter(
          (v) => v !== undefined && v !== null && v !== ""
        ).length;
        return answeredCount === totalQuestions;
      }

      // Type 8: Switch Card - ALL pairs must be matched
      case "8": {
        if (!Array.isArray(userAnswer)) return false;
        const totalPairs = currentQuestion.wordPairs?.length || 0;
        return userAnswer.length === totalPairs;
      }

      // Type 9: Parts of Speech - ALL words must be matched
      case "9": {
        if (typeof userAnswer !== "object" || Array.isArray(userAnswer))
          return false;
        const totalPairs = currentQuestion.wordPairs?.length || 0;
        return Object.keys(userAnswer).length === totalPairs;
      }

      // Type 3: Audio Listening - at least one selection
      case "3": {
        if (!Array.isArray(userAnswer)) return false;
        return userAnswer.length > 0;
      }

      // FIX: Type 13: True/False Selection - ALL statements must be answered
      case "13": {
        if (typeof userAnswer !== "object" || Array.isArray(userAnswer))
          return false;
        const totalStatements = currentQuestion.statements?.length || 0;
        if (totalStatements === 0) return true;
        // Check all statements have been answered (have boolean value)
        const answeredCount = Object.values(userAnswer).filter(
          (v) => v === true || v === false
        ).length;
        return answeredCount === totalStatements;
      }

      // Default: any non-empty answer is valid
      default: {
        if (Array.isArray(userAnswer)) {
          return userAnswer.length > 0;
        }
        if (typeof userAnswer === "object" && userAnswer !== null) {
          return Object.values(userAnswer).some(
            (v) => v !== undefined && v !== null && v !== ""
          );
        }
        return (
          userAnswer !== undefined && userAnswer !== null && userAnswer !== ""
        );
      }
    }
  }, [currentQuestion, userAnswers]);

  // Validation hint - only shown when validation fails
  const getValidationHint = useMemo((): string | null => {
    if (!currentQuestion) return null;
    if (isCurrentQuestionValid) return null;

    const userAnswer = userAnswers[currentQuestion.id];
    const questionType = currentQuestion.type;

    switch (questionType) {
      case "4": {
        const totalWords = currentQuestion.wordOptions?.length || 0;
        const placedCount = Array.isArray(userAnswer) ? userAnswer.length : 0;
        if (placedCount === 0) return `Place all ${totalWords} words`;
        return `${placedCount}/${totalWords} words placed`;
      }

      case "6": {
        const totalQuestions = currentQuestion.questions?.length || 0;
        const answeredCount =
          typeof userAnswer === "object" && !Array.isArray(userAnswer)
            ? Object.values(userAnswer).filter(
                (v) => v !== undefined && v !== null && v !== ""
              ).length
            : 0;
        if (answeredCount === 0)
          return `Answer all ${totalQuestions} questions`;
        return `${answeredCount}/${totalQuestions} answered`;
      }

      case "8": {
        const totalPairs = currentQuestion.wordPairs?.length || 0;
        const matchedCount = Array.isArray(userAnswer) ? userAnswer.length : 0;
        if (matchedCount === 0) return `Match all ${totalPairs} pairs`;
        return `${matchedCount}/${totalPairs} matched`;
      }

      case "9": {
        const totalPairs = currentQuestion.wordPairs?.length || 0;
        const matchedCount =
          typeof userAnswer === "object" && !Array.isArray(userAnswer)
            ? Object.keys(userAnswer).length
            : 0;
        if (matchedCount === 0) return `Match all ${totalPairs} words`;
        return `${matchedCount}/${totalPairs} matched`;
      }

      case "3":
        return "Select at least one answer";

      // FIX: Type 13 validation hint - show statements progress
      case "13": {
        const totalStatements = currentQuestion.statements?.length || 0;
        const answeredCount =
          typeof userAnswer === "object" && !Array.isArray(userAnswer)
            ? Object.values(userAnswer).filter((v) => v === true || v === false)
                .length
            : 0;
        if (answeredCount === 0)
          return `Answer all ${totalStatements} statements`;
        return `${answeredCount}/${totalStatements} statements answered`;
      }

      default:
        return "Please answer this question";
    }
  }, [currentQuestion, userAnswers, isCurrentQuestionValid]);

  // Check storage availability
  useEffect(() => {
    const isStorageAvailable = quizStorageService.hasStorageAvailable();
    if (!isStorageAvailable) {
      setStorageWarning(true);
      setStorageNotification({
        open: true,
        message:
          "Private browsing detected. Progress won't be saved between sessions.",
        severity: "warning",
        autoHideDuration: 8000,
      });
    }
  }, []);

  // Handle answer change
  const handleAnswerChange = useCallback(
    (answer: any) => {
      if (!currentQuestion) return;

      setUserAnswers((prev) => {
        const updated = { ...prev, [currentQuestion.id]: answer };
        answerManagerRef.current.saveAnswer(
          currentQuestion.id,
          currentQuestion.type,
          answer,
          currentQuestion
        );
        return updated;
      });
    },
    [currentQuestion]
  );

  // Validate completion before submission
  const validateCompletion = useCallback((): {
    isValid: boolean;
    message?: string;
    missingRequired: number;
  } => {
    if (missingRequired > 0) {
      return {
        isValid: false,
        message: `Complete ${missingRequired} more required question${
          missingRequired > 1 ? "s" : ""
        } before submitting`,
        missingRequired,
      };
    }
    return { isValid: true, missingRequired: 0 };
  }, [missingRequired]);

  // Auto-save quiz state
  const saveQuizState = useCallback(() => {
    if (validQuestions.length === 0 || quizCompleted) return;

    setLastSaveStatus("saving");

    const answersMap = new Map<string, any>();
    Object.entries(userAnswers).forEach(([key, value]) => {
      answersMap.set(key, value);
    });

    const quizState: QuizState = {
      moduleName,
      week,
      currentQuestionIndex,
      userAnswers: answersMap,
      startTime: startTimeRef.current,
      lastUpdate: Date.now(),
      quizId,
      totalQuestions: validQuestions.length,
      progressStats: {
        completed: progressStats.completed,
        skipped: progressStats.skipped,
        required: progressStats.required,
        requiredCompleted: progressStats.requiredCompleted,
      },
    };

    const result: SaveResult = quizStorageService.saveQuizState(quizState);

    if (result.success) {
      setLastSaveStatus("saved");
      setLastSaveTime(new Date());
    } else {
      setLastSaveStatus("error");
    }
  }, [
    moduleName,
    week,
    currentQuestionIndex,
    userAnswers,
    quizId,
    validQuestions,
    quizCompleted,
    progressStats,
  ]);

  // Auto-save with debounce
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      saveQuizState();
    }, 500);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [userAnswers, currentQuestionIndex, saveQuizState]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (isLoading) return;

    if (!isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      const completionCheck = validateCompletion();
      if (!completionCheck.isValid) {
        setStorageNotification({
          open: true,
          message: completionCheck.message || "Complete all required questions",
          severity: "warning",
          autoHideDuration: 5000,
        });
        return;
      }
      setShowCompletionDialog(true);
    }
  }, [isLastQuestion, isLoading, validateCompletion]);

  const handlePrevious = useCallback(() => {
    if (isLoading || isFirstQuestion) return;
    setCurrentQuestionIndex((prev) => prev - 1);
  }, [isFirstQuestion, isLoading]);

  // Skip question handler
  const handleSkipQuestion = useCallback(() => {
    if (!currentQuestion || currentQuestion.required !== false) return;
    setShowSkipDialog(true);
  }, [currentQuestion]);

  const confirmSkip = useCallback(() => {
    setShowSkipDialog(false);
    setUserAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: "SKIPPED",
    }));

    if (!isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      const completionCheck = validateCompletion();
      if (!completionCheck.isValid) {
        setStorageNotification({
          open: true,
          message: completionCheck.message || "Complete all required questions",
          severity: "warning",
          autoHideDuration: 5000,
        });
        return;
      }
      setShowCompletionDialog(true);
    }
  }, [currentQuestion, isLastQuestion, validateCompletion]);

  // Quiz completion
  const handleQuizComplete = useCallback(async () => {
    setQuizCompleted(true);
    setShowCompletionDialog(false);
    setIsSubmitting(true);
    setSubmissionError(null);

    const totalTime = Date.now() - startTimeRef.current;

    try {
      const sessionId = uuidv4();

      const apiAnswers: { [key: string]: string | number } = {};
      Object.entries(userAnswers).forEach(([questionId, answer]) => {
        if (
          answer === undefined ||
          answer === null ||
          answer === "" ||
          answer === "SKIPPED" ||
          answer === "VIEWED"
        ) {
          return;
        }

        if (Array.isArray(answer) || typeof answer === "object") {
          apiAnswers[questionId] = JSON.stringify(answer);
        } else {
          apiAnswers[questionId] = answer;
        }
      });

      const submissionData: QuizSubmissionData = {
        sessionId,
        moduleId,
        answers: apiAnswers,
        timeSpent: totalTime,
        completedAt: Date.now(),
      };

      const apiResult: QuizResult = await submitQuizAnswers(submissionData);

      quizStorageService.removeQuizState(quizId);
      onComplete(apiResult);
      setIsSubmitting(false);
    } catch (error: any) {
      setIsSubmitting(false);
      setQuizCompleted(false);

      let errorMessage = "Failed to submit quiz. Please try again.";
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = "Invalid quiz data. Please review your answers.";
            break;
          case 401:
          case 403:
            errorMessage = "Session expired. Please sign in again.";
            break;
          case 500:
            errorMessage = "Server error. Please try again in a moment.";
            break;
          default:
            errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Network error. Check your connection.";
      }

      setSubmissionError(errorMessage);
      setStorageNotification({
        open: true,
        message: errorMessage,
        severity: "error",
        autoHideDuration: 10000,
      });
    }
  }, [moduleId, userAnswers, quizId, onComplete]);

  // Exit handler
  const handleExit = useCallback(() => {
    setExitDialogOpen(true);
  }, []);

  const confirmExit = useCallback(() => {
    saveQuizState();
    onExit();
  }, [saveQuizState, onExit]);

  const handleCloseNotification = () => {
    setStorageNotification((prev) => ({ ...prev, open: false }));
  };

  // Direct navigation to question
  const navigateToQuestion = useCallback(
    (index: number) => {
      if (index >= 0 && index < validQuestions.length) {
        setCurrentQuestionIndex(index);
      }
    },
    [validQuestions.length]
  );

  // Validation checks
  if (!questions || questions.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">No questions available for this quiz.</Alert>
      </Container>
    );
  }

  if (!currentQuestion) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Storage Warning Banner */}
      {storageWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          Private browsing mode - progress saved only during this session.
        </Alert>
      )}

      {/* ============================================================ */}
      {/* SIMPLIFIED HEADER                                            */}
      {/* ============================================================ */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Title Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h1">
            {moduleName} - Week {week}
          </Typography>

          {/* Save Status */}
          {lastSaveStatus === "saving" && (
            <Chip
              icon={<CircularProgress size={14} />}
              label="Saving..."
              size="small"
              variant="outlined"
            />
          )}
          {lastSaveStatus === "saved" && lastSaveTime && (
            <Chip
              icon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
              label={`Saved ${lastSaveTime.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
          {lastSaveStatus === "error" && (
            <Chip
              icon={<ErrorOutline sx={{ fontSize: 16 }} />}
              label="Save failed"
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Box>

        {/* Single Progress Bar with Inline Stats */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Question {currentQuestionIndex + 1} of {validQuestions.length}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {progressStats.completed}/{validQuestions.length} completed
              </Typography>
              {!allRequiredDone && (
                <Chip
                  label={`${missingRequired} required left`}
                  size="small"
                  color="warning"
                  icon={<Warning sx={{ fontSize: 14 }} />}
                  sx={{ height: 24 }}
                />
              )}
              {allRequiredDone && (
                <Chip
                  label="Ready to submit"
                  size="small"
                  color="success"
                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                  sx={{ height: 24 }}
                />
              )}
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                backgroundColor: allRequiredDone
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
              },
            }}
          />
        </Box>

        {/* Simplified Dot Navigation */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {validQuestions.map((q, index) => {
            const answer = userAnswers[q.id];

            // FIX: Handle type 13 answer format (object with boolean values)
            let isAnswered = false;
            if (q.type === "12") {
              isAnswered = true;
            } else if (q.type === "13") {
              // Type 13: check if all statements answered
              if (typeof answer === "object" && !Array.isArray(answer)) {
                const totalStatements = q.statements?.length || 0;
                const answeredCount = Object.values(answer).filter(
                  (v) => v === true || v === false
                ).length;
                isAnswered = answeredCount === totalStatements;
              }
            } else if (Array.isArray(answer)) {
              isAnswered = answer.length > 0;
            } else {
              isAnswered =
                answer !== undefined &&
                answer !== null &&
                answer !== "" &&
                answer !== "SKIPPED" &&
                !(
                  typeof answer === "object" && Object.keys(answer).length === 0
                );
            }

            const isSkipped = answer === "SKIPPED";
            const isCurrent = index === currentQuestionIndex;
            const isRequired = q.required !== false;

            // Determine dot color
            let bgColor = theme.palette.grey[300]; // Default: unanswered
            if (isCurrent) {
              bgColor = theme.palette.primary.main;
            } else if (isAnswered) {
              bgColor = theme.palette.success.main;
            } else if (isSkipped) {
              bgColor = theme.palette.grey[400];
            }

            // Border for required questions that are not answered
            let borderStyle = "none";
            if (isRequired && !isAnswered && !isCurrent) {
              borderStyle = `2px solid ${theme.palette.warning.main}`;
            }

            return (
              <Tooltip
                key={q.id}
                title={`Q${index + 1}${isRequired ? " (Required)" : ""}${
                  isAnswered ? " ✓" : isSkipped ? " (Skipped)" : ""
                }`}
                arrow
                placement="top"
              >
                <Box
                  onClick={() => navigateToQuestion(index)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: bgColor,
                    border: borderStyle,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.15s, box-shadow 0.15s",
                    "&:hover": {
                      transform: "scale(1.15)",
                      boxShadow: theme.shadows[3],
                    },
                    color: "white",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  {isCurrent ? index + 1 : isAnswered ? "✓" : ""}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Paper>

      {/* Question Content */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Suspense fallback={<CircularProgress />}>
          <QuestionRenderer
            key={currentQuestion.id}
            question={currentQuestion}
            onAnswerChange={handleAnswerChange}
            userAnswer={userAnswers[currentQuestion.id]}
            disabled={reviewMode || quizCompleted}
            showResults={showResults}
            reviewMode={reviewMode}
          />
        </Suspense>
      </Paper>

      {/* Navigation Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ExitToApp />}
          onClick={handleExit}
          size="medium"
        >
          Save & Exit
        </Button>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Validation hint */}
          {!isCurrentQuestionValid && getValidationHint && (
            <Typography variant="body2" color="text.secondary">
              {getValidationHint}
            </Typography>
          )}

          {currentQuestion.required === false && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleSkipQuestion}
              disabled={isLoading}
              size="medium"
            >
              Skip
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<NavigateBefore />}
            onClick={handlePrevious}
            disabled={isFirstQuestion || isLoading}
            size="medium"
          >
            Previous
          </Button>

          <Button
            variant="contained"
            endIcon={isLastQuestion ? <CheckCircle /> : <NavigateNext />}
            onClick={handleNext}
            disabled={isLoading || !isCurrentQuestionValid}
            color={isLastQuestion ? "success" : "primary"}
            size="medium"
          >
            {isLastQuestion ? "Submit" : "Next"}
          </Button>
        </Box>
      </Box>

      {/* Storage Notification */}
      <Snackbar
        open={storageNotification.open}
        autoHideDuration={storageNotification.autoHideDuration}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={storageNotification.severity}
          variant="filled"
        >
          {storageNotification.message}
        </Alert>
      </Snackbar>

      {/* Skip Dialog */}
      <Dialog open={showSkipDialog} onClose={() => setShowSkipDialog(false)}>
        <DialogTitle>Skip This Question?</DialogTitle>
        <DialogContent>
          <Typography>
            This question is optional. You can come back to answer it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSkipDialog(false)}>Cancel</Button>
          <Button onClick={confirmSkip} color="warning">
            Skip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog
        open={showCompletionDialog}
        onClose={() => !isSubmitting && setShowCompletionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isSubmitting ? (
              <>
                <CircularProgress size={24} />
                Submitting...
              </>
            ) : submissionError ? (
              <>
                <ErrorOutline color="error" />
                Submission Failed
              </>
            ) : (
              <>
                <CheckCircle color="success" />
                Ready to Submit?
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {submissionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submissionError}
            </Alert>
          )}
          <List dense>
            <ListItem>
              <ListItemText
                primary="Questions Answered"
                secondary={`${progressStats.completed} of ${validQuestions.length}`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Time Spent"
                secondary={`${Math.floor(
                  (Date.now() - startTimeRef.current) / 60000
                )} minutes`}
              />
            </ListItem>
            {progressStats.skipped > 0 && (
              <ListItem>
                <ListItemText
                  primary="Skipped"
                  secondary={progressStats.skipped}
                  secondaryTypographyProps={{ color: "warning.main" }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          {!isSubmitting && (
            <Button onClick={() => setShowCompletionDialog(false)}>
              Review
            </Button>
          )}
          <Button
            onClick={handleQuizComplete}
            variant="contained"
            color={submissionError ? "warning" : "success"}
            disabled={isSubmitting || !allRequiredDone}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircle />
              )
            }
          >
            {isSubmitting
              ? "Submitting..."
              : submissionError
              ? "Retry"
              : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Dialog */}
      <Dialog open={exitDialogOpen} onClose={() => setExitDialogOpen(false)}>
        <DialogTitle>Save and Exit?</DialogTitle>
        <DialogContent>
          <Typography>
            Your progress will be saved and you can continue later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmExit} color="primary" variant="contained">
            Save & Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ModuleQuizComponent;
